import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ListChecks, CheckCircle, Circle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const GROUPS = [
  { title: "Core", match: (k) => ["credit_apps", "list_yourself", "public_records"].includes(k), tradeline: false },
  { title: "Primary Tradelines", match: (k) => k.startsWith("primary_tradeline_"), tradeline: true },
  { title: "AU Tradelines", match: (k) => k.startsWith("au_tradeline_"), tradeline: true },
];

const today = () => new Date().toISOString().slice(0, 10);

export function SpecialProfile({ clientId }) {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/cc/clients/${clientId}/special-profile`); setItems(data.items || []); }
    catch { /* ignore */ }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const save = async (it) => {
    if (!it) return;
    try {
      await api.put(`/cc/clients/${clientId}/special-profile/${it.key}`, {
        done: it.done, completed_date: it.completed_date || null,
        name: it.name || "", creditor: it.creditor || "", limit: it.limit || "", note: it.note || "",
      });
    } catch { toast.error("Failed to save"); load(); }
  };
  const apply = (key, patch, persist = true) => {
    let updated;
    setItems((r) => r.map((i) => { if (i.key === key) { updated = { ...i, ...patch }; return updated; } return i; }));
    if (persist && updated) save(updated);
  };
  const toggle = (it) => apply(it.key, { done: !it.done, completed_date: !it.done ? (it.completed_date || today()) : null });

  const doneCount = items.filter((i) => i.done).length;

  return (
    <div data-testid="special-profile-section">
      <div className="flex items-center gap-2 mb-4">
        <ListChecks size={20} className="text-primary" />
        <h2 className="text-lg font-medium">Special Profile</h2>
        <span className="text-xs text-muted-foreground">· {doneCount}/{items.length} complete</span>
      </div>
      <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="special-profile-list">
        {GROUPS.map((g) => {
          const groupItems = items.filter((i) => g.match(i.key));
          if (groupItems.length === 0) return null;
          return (
            <div key={g.title} className="p-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground px-1 mb-2">{g.title}</div>
              <div className="space-y-2">
                {groupItems.map((it) => (
                  <div key={it.key} className="rounded-lg border border-white/5 bg-zinc-950/40 px-3 py-2.5" data-testid={`special-item-${it.key}`}>
                    <div className="flex items-center justify-between gap-3">
                      {g.tradeline ? (
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <button onClick={() => toggle(it)} data-testid={`toggle-special-${it.key}`} className="shrink-0">
                            {it.done ? <CheckCircle size={20} weight="fill" className="text-emerald-500" /> : <Circle size={20} className="text-zinc-600" />}
                          </button>
                          <Input
                            placeholder={`${it.label} name`} value={it.name || ""}
                            onChange={(e) => apply(it.key, { name: e.target.value }, false)}
                            onBlur={() => save(items.find((x) => x.key === it.key))}
                            data-testid={`special-name-${it.key}`}
                            className={`h-8 bg-zinc-900/60 border-white/10 text-sm ${it.done ? "line-through text-muted-foreground" : ""}`}
                          />
                        </div>
                      ) : (
                        <button onClick={() => toggle(it)} className="flex items-center gap-3 min-w-0 text-left" data-testid={`toggle-special-${it.key}`}>
                          {it.done ? <CheckCircle size={20} weight="fill" className="text-emerald-500 shrink-0" /> : <Circle size={20} className="text-zinc-600 shrink-0" />}
                          <span className={`text-sm truncate ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.label}</span>
                        </button>
                      )}
                      <Input
                        type="date" value={it.completed_date || ""}
                        onChange={(e) => apply(it.key, { completed_date: e.target.value || null })}
                        data-testid={`special-date-${it.key}`}
                        className="h-8 w-40 bg-zinc-900/60 border-white/10 text-xs shrink-0"
                      />
                    </div>
                    {g.tradeline && (
                      <div className="grid gap-2 mt-2 sm:grid-cols-3">
                        <Input
                          placeholder="Creditor" value={it.creditor || ""}
                          onChange={(e) => apply(it.key, { creditor: e.target.value }, false)}
                          onBlur={() => save(items.find((x) => x.key === it.key))}
                          data-testid={`special-creditor-${it.key}`}
                          className="h-8 bg-zinc-900/60 border-white/10 text-xs"
                        />
                        <Input
                          placeholder="Limit" value={it.limit || ""}
                          onChange={(e) => apply(it.key, { limit: e.target.value }, false)}
                          onBlur={() => save(items.find((x) => x.key === it.key))}
                          data-testid={`special-limit-${it.key}`}
                          className="h-8 bg-zinc-900/60 border-white/10 text-xs"
                        />
                        <Input
                          placeholder="Note" value={it.note || ""}
                          onChange={(e) => apply(it.key, { note: e.target.value }, false)}
                          onBlur={() => save(items.find((x) => x.key === it.key))}
                          data-testid={`special-note-${it.key}`}
                          className="h-8 bg-zinc-900/60 border-white/10 text-xs"
                        />
                      </div>
                    )}
                    {!g.tradeline && (
                      <Input
                        placeholder="Note" value={it.note || ""}
                        onChange={(e) => apply(it.key, { note: e.target.value }, false)}
                        onBlur={() => save(items.find((x) => x.key === it.key))}
                        data-testid={`special-note-${it.key}`}
                        className="h-8 bg-zinc-900/60 border-white/10 text-xs mt-2"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
